import IBiometricProvider from './IBiometricProvider';

/**
 * Universal External Fingerprint Scanner Adapter.
 * Integrates external USB biometric scanners via a vendor-agnostic local device bridge.
 * 
 * Technical Design:
 * - Browser cannot directly communicate with arbitrary USB hardware drivers.
 * - This adapter communicates with a secured local bridge service (e.g. localhost:18080)
 *   or platform-level WebUSB/FIDO provider if installed.
 * - Zero Raw Biometric Exposure: The bridge performs template verification or
 *   cryptographic signature matching and returns only cryptographic proofs.
 * - Device Detection & Safe Fallback: If no bridge or scanner is present,
 *   isAvailable() safely returns false without interrupting normal LIMS flow.
 */
export class ExternalScannerAdapter extends IBiometricProvider {
  constructor(bridgeBaseUrl = 'https://127.0.0.1:18080/api/biometric') {
    super();
    this.bridgeBaseUrl = bridgeBaseUrl;
    this.providerName = 'External USB Biometric Scanner (Adapter)';
    this._isAvailableCache = null;
  }

  /**
   * Probes whether a supported external biometric scanner and local bridge service are accessible.
   */
  async isAvailable() {
    // Only query on explicit action or cached check to prevent console noise
    if (this._isAvailableCache !== null) {
      return this._isAvailableCache;
    }

    try {
      // Short timeout health-check to local bridge
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 800);

      const res = await fetch(`${this.bridgeBaseUrl}/status`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal,
      }).catch(() => null);

      clearTimeout(timeoutId);

      if (res && res.ok) {
        const data = await res.json();
        this._isAvailableCache = !!data.deviceConnected;
        return this._isAvailableCache;
      }
    } catch {
      // Local bridge not running - normal for devices without dedicated external USB scanner
    }

    this._isAvailableCache = false;
    return false;
  }

  /**
   * Retrieves external scanner hardware info from local bridge.
   */
  async getDeviceInfo() {
    const available = await this.isAvailable();
    return {
      providerName: this.providerName,
      deviceType: 'ExternalUSBScanner',
      supportsBiometrics: true,
      isAvailable: available,
    };
  }

  /**
   * Delegates enrollment ceremony to local bridge adapter.
   */
  async register(serverOptions) {
    const available = await this.isAvailable();
    if (!available) {
      throw new Error(
        'External fingerprint scanner is not connected or local biometric service is not running. Please use Windows Hello or password login.'
      );
    }

    const res = await fetch(`${this.bridgeBaseUrl}/enroll`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serverOptions),
    });

    if (!res.ok) {
      throw new Error('External scanner enrollment failed or was cancelled at device.');
    }

    return await res.json();
  }

  /**
   * Delegates authentication ceremony to local bridge adapter.
   */
  async authenticate(serverOptions) {
    const available = await this.isAvailable();
    if (!available) {
      throw new Error(
        'Secure fingerprint authentication is not available on this device. Please use another supported authentication method.'
      );
    }

    const res = await fetch(`${this.bridgeBaseUrl}/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serverOptions),
    });

    if (!res.ok) {
      throw new Error('External scanner verification failed.');
    }

    return await res.json();
  }
}

export default ExternalScannerAdapter;
